// DocumentosPanelXL - Sprint 44 Fase 3 Vista 10.
// Sube/lista/elimina adjuntos de una importacion. Tokens + dark.
import { useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useAdjuntosXL from './hooks/useAdjuntosXL';
import { adjuntoHref } from './services/adjuntosService';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import XLBadge from '../../components/xl/XLBadge';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { TIPO_ADJUNTO_LABEL } from './utils';

const CSS_ID = 'xl-imp-docs-css';
const CSS = `
.xl-docs { display: flex; flex-direction: column; gap: 20px; font-family: var(--xl-font-sans) }
.xl-docs__placeholder { padding: 16px; color: var(--xl-color-text-muted); font-size: 13px }

.xl-docs__form {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 16px;
  display: flex; flex-direction: column; gap: 12px;
}
.xl-docs__form-row {
  display: grid; grid-template-columns: 2fr 1fr; gap: 12px;
}
@media (max-width: 720px) { .xl-docs__form-row { grid-template-columns: 1fr } }

.xl-docs__file-wrap { display: flex; flex-direction: column; gap: 4px }
.xl-docs__file-label {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
}
.xl-docs__file {
  font-size: 13px; color: var(--xl-color-text);
}
.xl-docs__file::file-selector-button {
  font: 500 12px/1 var(--xl-font-sans);
  padding: 8px 12px; margin-right: 10px;
  border: none; border-radius: var(--xl-radius-md);
  background: rgba(37,99,235,0.10); color: var(--xl-color-info);
  cursor: pointer;
}
.xl-docs__file::file-selector-button:hover { background: rgba(37,99,235,0.18) }
[data-xl-theme="dark"] .xl-docs__file::file-selector-button { background: rgba(96,165,250,0.18) }

.xl-docs__btn {
  align-self: flex-end;
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
  cursor: pointer;
}
.xl-docs__btn:hover { background: var(--xl-color-primary-hover) }
.xl-docs__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-docs__btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-docs__err {
  font-size: 12px; padding: 8px 12px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28); border-radius: var(--xl-radius-sm);
}
.xl-docs__list-title {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text-muted); margin-bottom: 8px;
}
.xl-docs__items {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  overflow: hidden;
}
.xl-docs__item {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 14px;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-docs__item:last-child { border-bottom: 0 }
.xl-docs__item-name {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: var(--xl-color-text);
  text-decoration: none; min-width: 0;
}
.xl-docs__item-name:hover { color: var(--xl-color-info) }
.xl-docs__item-filename {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.xl-docs__item-meta {
  font-size: 11px; color: var(--xl-color-text-muted); margin-top: 2px;
}
.xl-docs__del {
  background: transparent; border: none; cursor: pointer;
  color: var(--xl-color-danger); font-size: 12px;
  padding: 4px 8px; border-radius: var(--xl-radius-sm);
}
.xl-docs__del:hover { background: rgba(220,38,38,0.08) }
.xl-docs__empty {
  padding: 28px 16px; text-align: center;
  color: var(--xl-color-text-muted); font-size: 13px;
  border: 1px dashed var(--xl-color-border);
  border-radius: var(--xl-radius-md);
}
`;

function fmtBytes(n) {
  if (n === null || n === undefined || n === '') return '';
  const v = Number(n);
  if (Number.isNaN(v)) return '';
  if (v < 1024) return v + ' B';
  if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB';
  return (v / 1024 / 1024).toFixed(2) + ' MB';
}

export default function DocumentosPanelXL({ importacionId, isNew }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const enabled = !isNew && !!importacionId;
  const { items, tipos, loading, error, upload, remove } =
    useAdjuntosXL(enabled ? importacionId : null);

  const [file, setFile]     = useState(null);
  const [tipo, setTipo]     = useState('factura_proveedor');
  const [notas, setNotas]   = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);

  if (!enabled) {
    return (
      <div className="xl-docs__placeholder">
        Guarda la importacion primero para habilitar la carga de documentos.
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setUploadErr('Selecciona un archivo'); return; }
    setSaving(true);
    setUploadErr(null);
    try {
      await upload({ file, tipoDocumento: tipo, notas: notas || null });
      setFile(null); setNotas('');
      if (e.target && e.target.reset) e.target.reset();
    } catch (err) {
      setUploadErr((err && err.message) || 'Error al subir');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try { await remove(id); }
    catch (err) { alert((err && err.message) || 'Error al eliminar'); }
  };

  const tiposDisponibles = (tipos && tipos.length) ? tipos : Object.keys(TIPO_ADJUNTO_LABEL);
  const tipoOptions = tiposDisponibles.map((k) => ({
    value: k, label: TIPO_ADJUNTO_LABEL[k] || k,
  }));

  return (
    <div className="xl-docs">
      <form onSubmit={onSubmit} className="xl-docs__form">
        <div className="xl-docs__form-row">
          <div className="xl-docs__file-wrap">
            <span className="xl-docs__file-label">
              Archivo (PDF, JPG, PNG, WEBP \u2014 max 5MB)
            </span>
            <input type="file" className="xl-docs__file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setFile(e.target.files && e.target.files[0])} />
          </div>
          <XLSelect label="Tipo" value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            options={tipoOptions} />
        </div>
        <XLInput label="Notas (opcional)" value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder="Ej: factura original del proveedor" />
        {uploadErr && <div className="xl-docs__err" role="alert">{uploadErr}</div>}
        <button type="submit" className="xl-docs__btn"
          disabled={saving || !file}>
          {saving ? 'Subiendo\u2026' : 'Subir documento'}
        </button>
      </form>

      <div>
        <div className="xl-docs__list-title">Documentos ({items.length})</div>

        {loading && <div className="xl-docs__placeholder"><XLSkeleton variant="rect" height={60} /></div>}
        {error && <div className="xl-docs__err" role="alert">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="xl-docs__empty">No hay documentos subidos aun.</div>
        )}
        {items.length > 0 && (
          <ul className="xl-docs__items" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((a) => (
              <li key={a.id} className="xl-docs__item">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <a href={adjuntoHref(a.url_archivo)} target="_blank" rel="noreferrer"
                    className="xl-docs__item-name" title={a.nombre_archivo}>
                    <XLBadge variant="info">
                      {TIPO_ADJUNTO_LABEL[a.tipo_documento] || a.tipo_documento}
                    </XLBadge>
                    <span className="xl-docs__item-filename">{a.nombre_archivo}</span>
                  </a>
                  <div className="xl-docs__item-meta">
                    {a.fecha_subida && new Date(a.fecha_subida).toLocaleString('es-CO')}
                    {a.tamano_bytes ? (' \u00b7 ' + fmtBytes(a.tamano_bytes)) : ''}
                    {a.notas ? (' \u00b7 ' + a.notas) : ''}
                  </div>
                </div>
                <button type="button" className="xl-docs__del" onClick={() => onDelete(a.id)}>
                  Eliminar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
